<template>
	<div>
		<v-data-table
			:headers="headers"
			:items="rights"
			class="elevation-1"
			:items-per-page="-1"
			item-key="right_id"
			>
			<template v-slot:item.mandator_description="{ item }">
				<div v-if="item.mandator_description">
					<strong>{{txt.restricted}}</strong> {{item.mandator_description}}
				</div>
				<div v-if="!item.mandator_description"> <strong>{{txt.notRestricted}}</strong></div>
			</template>
			<template v-slot:item.serviceprovider_id="{ item }">
				<div v-if="item.serviceprovider_id === null"> - </div>
				<div v-if="item.serviceprovider_id !== null" v-for="sp in item.serviceprovider_id"> {{ sp }} </div>
			</template>
			<template v-slot:item.product_sku="{ item }">
				<div v-if="item.product_sku === null"> - </div>
				<div v-if="item.product_sku !== null" v-for="product in item.product_sku"> {{ product }} </div>
			</template>
			<template v-slot:item.region_id="{ item }">
				<div v-if="item.region_id === null"> - </div>
				<div v-if="item.region_id !== null" v-for="region in item.region_id"> {{ region }} </div>
			</template>
			<template v-slot:item.reseller_id="{ item }">
				<div v-if="item.reseller_id === null"> - </div>
				<div v-if="item.reseller_id !== null" v-for="reseller in item.reseller_id"> {{ reseller }} </div>
			</template>
			<template v-slot:item.website_id="{ item }">
				<div v-if="item.website_id === null"> - </div>
				<div v-if="item.website_id !== null" v-for="website in item.website_id"> {{ website }} </div>
			</template>
			<template v-slot:item.store_group_id="{ item }">
				<div v-if="item.store_group_id === null"> - </div>
				<div v-if="item.store_group_id !== null" v-for="storeGroup in item.store_group_id"> {{ storeGroup }} </div>
			</template>
			<template v-slot:item.managed_by="{ item }">
				<v-tooltip bottom v-if="item.managed_by">
					<template v-slot:activator="{ on, attrs }">
						<v-chip small color="primary" outlined v-bind="attrs" v-on="on">
							{{ item.managed_by }}
						</v-chip>
					</template>
					<span>{{ managedTooltip(item) }}</span>
				</v-tooltip>
				<div v-else> - </div>
			</template>
			<template v-slot:item.actions="{ item }">
				<!-- ARC-1124: an externally managed right is overwritten wholesale on the owning
				     system's next reconcile, so editing it here would silently be undone. -->
				<v-tooltip bottom v-if="item.managed_by">
					<template v-slot:activator="{ on, attrs }">
						<span v-bind="attrs" v-on="on">
							<v-icon class="mr-3" small disabled>mdi-pencil</v-icon>
							<v-icon small disabled>mdi-delete</v-icon>
						</span>
					</template>
					<span>{{ txt.managedReadOnly.replace('{system}', item.managed_by) }}</span>
				</v-tooltip>
				<template v-else>
					<v-icon
						class="mr-3"
						small
						@click="editRight(item)"
						>
						mdi-pencil
					</v-icon>
					<v-icon
						small
						@click="deleteRight(item)"
						>
						mdi-delete
					</v-icon>
				</template>
			</template>
		</v-data-table>

		<dialog-before-action
			:maxWidthOfPanel="400"
			:header="txt.dialog.removeHeader"
			:model="show.deleteRightDialog"
			:key="show.deleteRightDialog"
			:payload="clickedItem"
			@primaryConfirmed="deleteRightConfirmed"
			@secondaryConfirmed="hideDialog"
		></dialog-before-action>

		<v-dialog
			v-model="show.editRightDialog"
			max-width="90%"
		>
			<v-card>
				<progress-bar :is-loading="loading"/>
				<special-user-rights
					:installation="selectedInstallationToEdit"
					:application="selectedApplicationToEdit"
					:selected-user="selectedUser"
					:specialRightsToggle="show.editRightDialog"
					:rightsToPrefill="rights.filter((right) => right.right_id == clickedItem.right_id)"
					@serviceproviderChanged="serviceprovidertRightsUpdate"
					@productChanged="productRightsUpdate"
					@regionChanged="regionRightsUpdate"
					@salesChannelChanged="salesChannelRightsUpdate"
					@resellerChanged="resellerRightsUpdate"
					@loadingChanged="(value) => loading = value"
				/>
				<confirm-reject-btns
					class="current-rights-confirm-reject-section"
					:labelPrimaryAction="txt.btn.confirm"
					:labelSecondaryAction="txt.btn.cancel"
					@confirmed="confirmChange"
					@rejected="() => show.editRightDialog = false"
				/>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import DialogBeforeAction from '../../arcCustom/DialogBeforeAction.vue'
import SpecialUserRights from './SpecialUserRights.vue'
import ConfirmRejectBtns from '../../arcCustom/ConfirmRejectBtns.vue'
import ProgressBar from '../../vueBasics/Progressbar.vue'

export default {
	name: 'CurrentRights',
	components: {
		DialogBeforeAction,
		SpecialUserRights,
		ConfirmRejectBtns,
		ProgressBar,
	},
	props: {
		rights: {
			type: Array,
		},
		selectedUser: {
			type: Object
		}
	},
	data() {
		return {
			txt: {
				restricted: 'Restricted to: ',
				notRestricted: 'Admin rights',
				onReport: 'on report',
				managedReadOnly: 'Managed by {system} - change it there. Edits made here would be overwritten on the next sync.',
				btn: {
					remove: 'Remove',
					confirm: 'Confirm',
					cancel: 'Cancel',
				},
				dialog: {
					removeHeader: 'Removing right?',
				},
				headers: {
					serviceprovider: 'Service provider',
					product: 'Product',
					region: 'Region',
					reseller: 'Reseller',
					websiteSalesChannel: 'Website / sales channel',
					storeGroup: 'Store group',
					managedBy: 'Managed by',
					report: 'Report',
					right: 'Right',
					remove: 'Remove',
				},
			},
			show: {
				deleteRightDialog: false,
				editRightDialog: false,
			},
			clickedItem: '',
			selectedApplicationToEdit: {}, 
			selectedInstallationToEdit: {}, 
			loading: false,
			editedRights: {},
		}
	},
	computed: {
		headers() {
			const headers = [
				{ text: this.txt.headers.right, value: 'mandator_description' },
				{ text: this.txt.headers.report, value: 'application_name' },
				{ text: this.txt.headers.serviceprovider, align: 'start', value: 'serviceprovider_id' },
				{ text: this.txt.headers.product, value: 'product_sku' },
				{ text: this.txt.headers.region, value: 'region_id' },
				{ text: this.txt.headers.reseller, value: 'reseller_id' },
				{ text: this.txt.headers.websiteSalesChannel, value: 'website_id' },
				{ text: this.txt.headers.storeGroup, value: 'store_group_id' },
				{ text: this.txt.headers.managedBy, value: 'managed_by' },
				{ text: this.txt.headers.remove, value: 'actions', sortable: false },
			]
			return headers
		},
	},
	methods: {
		managedTooltip(item) {
			const parts = [`Managed by ${item.managed_by}`]
			if (item.managed_external_id) parts.push(`external id ${item.managed_external_id}`)
			if (item.managed_at) parts.push(`last synced ${new Date(item.managed_at).toLocaleString()}`)
			return parts.join(' - ')
		},
		deleteRight(item) {
			this.clickedItem = item
			this.show.deleteRightDialog = true
		},
		deleteRightConfirmed(itemClicked) {
			this.show.deleteRightDialog = false
			window.scrollTo(0, 0)
			this.$emit('deleteRight', itemClicked)
		},
		hideDialog() {
			this.show.deleteRightDialog = false
		},
		confirmChange() {
			const editedRightsObject = {
				rightBase: this.rights.filter((right) => right.right_id == this.clickedItem.right_id),
				editedSpecialRights: this.editedRights,
			}
			this.$emit('confirmedChange', editedRightsObject)
			this.show.editRightDialog = false
		},
		editRight(item) {
			this.selectedApplicationToEdit.application_shortname = item.application_shortname
			this.selectedInstallationToEdit.installation_id = (item.installation_id) ? item.installation_id : 'admin'
			this.clickedItem = item
			this.show.editRightDialog = true
		},
		serviceprovidertRightsUpdate(values) {
			if (!values) return
			let rightToUpdate = this.rights.filter((right) => right.right_id == this.clickedItem.right_id)[0]
			this.editedRights.serviceprovider_id = values.map(right => right.id)
		},
		productRightsUpdate(values) {
			if (!values) return
			// due ARC-572 
			let rightToUpdate = this.rights.filter((right) => right.right_id == this.clickedItem.right_id)[0]
			if (rightToUpdate.application_shortname == 'rr') {
				this.editedRights.product_sku = values.map(right => right.sku)
				return
			}
			this.editedRights.product_sku = values.map(right => right.entity_id)
		},
		regionRightsUpdate(values) {
			if (!values) return
			let rightToUpdate = this.rights.filter((right) => right.right_id == this.clickedItem.right_id)[0]
			this.editedRights.region_id = values.map(right => right.id)
		},
		salesChannelRightsUpdate(values) {
			if (!values) return
			let rightToUpdate = this.rights.filter((right) => right.right_id == this.clickedItem.right_id)[0]
			this.editedRights.website_id = values.map(right => right.website_id)
		},
		resellerRightsUpdate(values) {
			if (!values) return
			let rightToUpdate = this.rights.filter((right) => right.right_id == this.clickedItem.right_id)[0]
			this.editedRights.reseller_id = values.map(right => right.id)
		},
	},
}
</script>

<style scoped>
.current-rights-confirm-reject-section {
	padding-bottom: 20px !important;
}

</style>
